/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import React from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { Checkbox } from '@atlaskit/checkbox';
import { jsx } from '@atlaskit/css';
import { CheckboxField, Field } from '@atlaskit/form';
import { fg } from '@atlaskit/platform-feature-flags';
import Suspense from '@atlaskit/react-ufo/suspense';
import TextArea from '@atlaskit/textarea';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useSetMessageFeedbackSent } from '@atlassian/conversation-assistant-store/controllers/message-feedback';
import { SmartCreateFeedback } from '@atlassian/smart-create-feedback/smart-create-feedback';
import { SmartCreateFeedbackVariant } from '@atlassian/smart-create-feedback/types';

import { getCurrentOrigin } from '../../common/utils/date-time-util/getCurrentOrigin';
import { useGlobalConfig } from '../../common/utils/global-config/useGlobalConfig';
import { useUIConfig } from '../../common/utils/ui-config/useUIConfig';
import { useConversation } from '../../controllers/conversation/useConversation';
import { useFeedbackCollector } from '../../controllers/feedback-collector/useFeedbackCollector';
import { useMessageHistory } from '../../controllers/message-history';
import { mapAnswerQualityToFeedbackValue } from '../../services/feedback/mapAnswerQualityToFeedbackValue';
import { sendConvoAIFeedback } from '../../services/feedback/sendConvoAIFeedback';

import { AsyncFeedbackFlagComponent } from './feedback-flag/async';
import { messages } from './messages';

const AkFeedbackCollector = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/conversation-assistant/feedback-collector" */ '@atlaskit/feedback-collector'
	).then((module) => {
		return {
			default: module.default,
		};
	}),
);

export const FeedbackCollector = (): JSX.Element => {
	di(useFeedbackCollector);
	const initialCanAttachMessages = fg('rovo_can_attach_user_messages_enabled');
	const { formatMessage } = useIntl();
	const {
		profileData: { name, aaid, email },
		feedbackCollector: { entryPointId, displaySubmitFlag },
		productKey,
		productHeader,
		experienceId,
		assistanceServiceConfig,
		siteId,
	} = useGlobalConfig();
	const [description, setDescription] = React.useState<string>('');
	const [displayFlag, setDisplayFlag] = React.useState<boolean>(false);
	const { sendAnalyticsEvent } = useAnalytics();
	const [canAttachMessages, setCanAttachMessages] = React.useState(initialCanAttachMessages);
	const { elements } = useUIConfig();
	const isInStagingArea = !!elements?.stagingAreaEnabled;

	const [
		{
			isOpen,
			feedbackData: { answerQuality, assistantResponseId, messageRequestId, feedbackSource },
		},
		{ close },
	] = useFeedbackCollector();
	const isGoodFeedback = answerQuality === 'good';
	const setFeedbackSent = useSetMessageFeedbackSent();

	const retrievedHistory = useMessageHistory({
		canAttachMessages,
		answerId: assistantResponseId,
	});

	// pass only selected agent id when in private mode,
	// this is needed for sales360 team to filter feedback issues given an agentId
	const { selectedAgent, conversation } = useConversation();
	const conversationId = conversation?.id;
	const selectedAgentId = selectedAgent?.id;
	const selectedAgentExternalConfigReference = selectedAgent?.external_config_reference;

	const selectedAgentMeta = React.useMemo(
		() => ({
			author_id: selectedAgentId,
			agent_external_config_reference: selectedAgentExternalConfigReference,
		}),
		[selectedAgentId, selectedAgentExternalConfigReference],
	);

	const history = canAttachMessages ? retrievedHistory : 'private';

	const sendMetadata = {
		aaid,
		answerQuality,
		assistantResponseId,
		request_id: messageRequestId || 'not-found',
		origin: getCurrentOrigin(),
		history,
		feedbackSource:
			(feedbackSource ?? fg('aifc_create_enabled'))
				? `${productKey}-v1${elements?.stagingAreaEnabled ? '-staging' : ''}`
				: undefined,
		selected_agent: selectedAgentMeta,
		minionAlias: assistanceServiceConfig?.minionAlias,
	};

	const resetForm = () => {
		setDescription('');
		setCanAttachMessages(initialCanAttachMessages);
	};

	const onSubmit = () => {
		sendAnalyticsEvent({
			action: 'submit',
			actionSubject: 'button',
			actionSubjectId: 'feedbackCollectorSubmitButton',
			eventType: 'ui',
			attributes: {
				canAttachMessages,
			},
		});

		setDisplayFlag(true);

		if (assistantResponseId) {
			setFeedbackSent(assistantResponseId);

			// Send feedback to Convo AI
			if (conversationId && !fg('service_agent_send_feedback_to_convo_ai_on_thumb')) {
				sendConvoAIFeedback({
					conversationId,
					messageId: assistantResponseId,
					feedbackValue: mapAnswerQualityToFeedbackValue(answerQuality),
					productHeader,
					experienceId,
					channelId: assistanceServiceConfig?.channelId ?? 'rovo-chat',
					cloudId: siteId,
				}).catch(() => {
					// TODO RSA-569: send analytics event for feedback to Convo AI failed
				});
			}
		}
	};

	const onClose = () => {
		sendAnalyticsEvent({
			action: 'close',
			actionSubject: 'button',
			actionSubjectId: 'feedbackCollectorCloseButton',
			eventType: 'ui',
		});

		// This is needed due to a bug in the component
		// Error: Can't perform a React state update on an unmounted component.
		// https://bitbucket.org/atlassian/atlassian-frontend/src/14852d9dc3bac13a97ff29899516a6af6f6e91e1/packages/web-platform/feedback-collector/src/components/FeedbackCollector.tsx#lines-448:460
		setTimeout(() => {
			close();
			resetForm();
		}, 0);
	};

	const historyCheckbox = (
		<CheckboxField
			name="conversation-assistant-feedback-attach-messages"
			defaultIsChecked={canAttachMessages}
		>
			{({ fieldProps }) => (
				<Checkbox
					{...fieldProps}
					label={formatMessage(messages.consentMessageHistoryCheckboxLabel)}
					onChange={(event) => setCanAttachMessages(event.target.checked)}
				/>
			)}
		</CheckboxField>
	);

	const feedbackTextArea = (
		<Field
			name="feedback-summary"
			label={formatMessage(messages.feedbackTextAreaLabel)}
			isRequired={!isGoodFeedback}
			defaultValue=""
		>
			{({ fieldProps }) => (
				<TextArea
					{...fieldProps}
					name="feedback-collector-description"
					minimumRows={6}
					placeholder={formatMessage(messages.feedbackTextAreaPlaceholder)}
					onChange={(e) => setDescription(e.target.value)}
					value={description}
				/>
			)}
		</Field>
	);

	const getDescriptionWithMetadata = () => `
      ${description}

      \`\`\`
      ${JSON.stringify(sendMetadata, null, 2)}
      \`\`\`
    `;

	return (
		<>
			{isOpen && isInStagingArea && expValEquals('cc-mui-slides-experiment', 'isEnabled', true) && (
				<SmartCreateFeedback
					variant={SmartCreateFeedbackVariant.CREATE_WITH_ROVO}
					onClose={onClose}
					onSubmit={onSubmit}
					tenantId={siteId}
					showDefaultTextFields={false}
					summaryDefaultValue={description}
					descriptionDefaultValue={getDescriptionWithMetadata()}
					customContent={
						<>
							{feedbackTextArea}
							{historyCheckbox}
						</>
					}
				/>
			)}
			{isOpen &&
				(!isInStagingArea || !expValEquals('cc-mui-slides-experiment', 'isEnabled', true)) && (
					<Suspense
						interactionName="conversation-assistant-feedback-collector-suspense"
						fallback={<></>}
					>
						<AkFeedbackCollector
							entrypointId={entryPointId}
							onClose={onClose}
							onSubmit={onSubmit}
							atlassianAccountId={aaid}
							name={name}
							email={email}
							showTypeField={false}
							descriptionDefaultValue={getDescriptionWithMetadata()}
							summaryDefaultValue={description} // This is needed due to a bug in the component
							showDefaultTextFields={false}
							customContent={
								<>
									{feedbackTextArea}
									{historyCheckbox}
								</>
							}
						/>
					</Suspense>
				)}
			{displaySubmitFlag && (
				<AsyncFeedbackFlagComponent displayFlag={displayFlag} setDisplayFlag={setDisplayFlag} />
			)}
		</>
	);
};
